"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Phone, Mail, Star, TrendingUp, Search, Building } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  title: "100% Direct Access",
  desc: "You work directly with me — no account managers or junior trainees."
}, {
  title: "Warrington & Cheshire Focus",
  desc: "Tailored local strategies targeting high-intent local customers."
}, {
  title: "Transparent Deliverables",
  desc: "Clear reporting on local map rankings, traffic, and real leads."
}];
const keyMetrics = [{
  label: "Direct Access",
  value: "100%"
}, {
  label: "Local Focus",
  value: "Cheshire"
}, {
  label: "SEO Strategy",
  value: "Custom"
}];
export default function Localhero() {
  return <section id="localhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 sm:py-20 lg:py-28">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-primary-foreground/5 blur-3xl pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Hero Copy & Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6 sm:space-y-8">
            
            {/* Badges / Pill Identifiers */}
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="border-accent/50 bg-accent/15 text-primary-foreground px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full shadow-xs backdrop-blur-sm">
                <MapPin className="size-3.5 text-accent shrink-0" />
                <span>Local SEO Consultant in Warrington</span>
              </Badge>
              <Badge variant="outline" className="border-white/20 bg-white/10 text-primary-foreground px-3 py-1.5 text-xs font-medium rounded-full hidden sm:inline-flex">
                <span>Direct 1-on-1 Consultancy</span>
              </Badge>
            </div>

            {/* Main H1 Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.12]">
              Local SEO Consultant in <span className="text-accent underline decoration-accent/40 underline-offset-8">Warrington</span>
            </h2>

            {/* Lead Narrative */}
            <p className="text-base sm:text-lg lg:text-xl text-primary-foreground/90 font-sans leading-relaxed max-w-2xl">
              I help Warrington and Cheshire businesses dominate Google Map Packs and organic local search. When you work with YE Consultancy, you deal directly with me — Yassine Essaadi — delivering tailored SEO strategies designed for real phone calls, visits, and qualified local leads.
            </p>

            {/* Bullet Trust Signals */}
            <div className="w-full space-y-3 pt-2">
              {trustHighlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 rounded-full bg-accent/20 p-1 text-accent shrink-0">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-white text-sm sm:text-base">{item.title}: </span>
                    <span className="text-primary-foreground/80 text-sm sm:text-base">{item.desc}</span>
                  </div>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 py-6 text-base rounded-lg shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-5 ml-1.5" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 dark:bg-transparent font-medium px-6 py-6 text-base rounded-lg transition-all">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>

            {/* Direct Contact Micro-bar */}
            <div className="pt-4 border-t border-white/15 w-full flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-primary-foreground/80">
              <div className="flex items-center gap-2">
                <Mail className="size-4 text-accent" />
                <a href="mailto:contact@ye-consultancy.com" className="hover:underline text-white font-medium">
                  contact@ye-consultancy.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="size-4 text-accent" />
                <a href="tel:+447490984430" className="hover:underline text-white font-medium">
                  +44 7490984430
                </a>
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-emerald-400" />
                <span>No Lock-In Contracts</span>
              </div>
            </div>

          </div>

          {/* Right Column: Consultant Profile & Warrington Authority Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Profile Card Surface */}
              <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-6 sm:p-8 shadow-2xl space-y-6 relative z-10">
                
                {/* Header: Photo & Direct Title */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <SafeHydrate><Avatar className="size-20 sm:size-24 border-2 border-accent shadow-md">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Freelance Local SEO Consultant in Warrington" />
                      <AvatarFallback className="bg-accent text-accent-foreground text-xl font-bold">YE</AvatarFallback>
                    </Avatar></SafeHydrate>
                    <span className="absolute bottom-0 right-0 size-4 rounded-full bg-emerald-500 border-2 border-primary" title="Available for consultation" />
                  </div>

                  <div>
                    <h2 className="font-serif text-xl sm:text-2xl font-bold text-white">Yassine Essaadi</h2>
                    <p className="text-sm text-accent font-medium">Independent SEO Strategy Consultant</p>
                    <div className="flex items-center gap-1 mt-1 text-amber-300 text-xs">
                      <Star className="size-3.5 fill-amber-300 text-amber-300" />
                      <Star className="size-3.5 fill-amber-300 text-amber-300" />
                      <Star className="size-3.5 fill-amber-300 text-amber-300" />
                      <Star className="size-3.5 fill-amber-300 text-amber-300" />
                      <Star className="size-3.5 fill-amber-300 text-amber-300" />
                      <span className="ml-1 text-primary-foreground/90 font-medium text-xs">Direct Hands-On Expertise</span>
                    </div>
                  </div>
                </div>

                <Separator className="bg-white/15" />

                {/* Direct Consultant Promise Box */}
                <div className="rounded-lg bg-black/20 border-l-4 border-accent p-4 space-y-1.5">
                  <p className="text-xs uppercase tracking-wider text-accent font-semibold">My Personal Promise</p>
                  <p className="text-sm text-primary-foreground/90 italic leading-relaxed">
                    {"\"I perform every SEO audit, map pack optimization, and local strategy myself. You will never be handed off to a junior executive or an automated template.\""}
                  </p>
                </div>

                {/* Key Metrics / Attributes */}
                <div className="grid grid-cols-3 gap-3 text-center pt-1">
                  {keyMetrics.map((metric, idx) => <div key={idx} data-index={idx} className="rounded-lg bg-white/5 border border-white/10 p-3">
                      <div className="text-lg font-bold text-white font-serif">{metric.value}</div>
                      <div className="text-[13px] text-primary-foreground/70 leading-tight mt-0.5">{metric.label}</div>
                    </div>)}
                </div>

                {/* Local Area Coverage Pills */}
                <div className="space-y-2 pt-1">
                  <div className="text-xs font-semibold text-primary-foreground/90 flex items-center gap-1.5">
                    <Building className="size-3.5 text-accent" />
                    <span>Serving Businesses Across Cheshire:</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs text-primary-foreground/80">
                    <span className="bg-white/10 px-2 py-1 rounded-md">Warrington</span>
                    <span className="bg-white/10 px-2 py-1 rounded-md">Stockton Heath</span>
                    <span className="bg-white/10 px-2 py-1 rounded-md">Lymm</span>
                    <span className="bg-white/10 px-2 py-1 rounded-md">Birchwood</span>
                    <span className="bg-white/10 px-2 py-1 rounded-md">Great Sankey</span>
                    <span className="bg-white/10 px-2 py-1 rounded-md">Chester</span>
                  </div>
                </div>

                {/* Audit Teaser Action */}
                <div className="pt-2">
                  <Button asChild variant="outline" className="w-full bg-white/10 hover:bg-white/20 border-white/20 text-white font-medium text-sm py-5 justify-center">
                    <Link to="/ContactPage">
                      <Search className="size-4 mr-2 text-accent" />
                      <span>Request Your Warrington Local SEO Audit</span>
                    </Link>
                  </Button>
                </div>

              </div>

              {/* Floating Floating Metric Pill */}
              <div className="absolute -bottom-4 -right-2 sm:-right-4 rounded-xl bg-accent text-accent-foreground p-3.5 shadow-xl border border-white/20 z-20 flex items-center gap-3">
                <div className="rounded-full bg-accent-foreground/10 p-2 text-accent-foreground">
                  <TrendingUp className="size-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-accent-foreground/80">Google Maps</div>
                  <div className="text-sm font-bold">Top 3 Map Pack Target</div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}